import { useState } from "react";
import { Link } from "react-router-dom";
import { Banner, Button, Field, Page } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { client, unwrap } from "../../api/client";
import { ApiError } from "../../api/errors";
import { useFeatures } from "../../api/features";
import { t } from "../../i18n/t";

/**
 * P1a §7: `POST /api/v1/auth/forgot-password` always answers `202` (anti-enumeration), so a 2xx has exactly one
 * success state and it is shown whether or not the address has an account. A FAILED request (403, 5xx, network)
 * is not that: nothing was sent, so it is an error, never the success text (xmg-stale-cookie).
 */
export function ForgotPasswordScreen(): JSX.Element {
  const { features } = useFeatures();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unwrap(client.POST("/api/v1/auth/forgot-password", { body: { email } }));
      setSubmitted(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("auth.login.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else {
        // The server never answers "no such account" with an error (it is a 202), so a thrown error here says
        // nothing about the address - it only means no mail was queued. Say so.
        setError(t("auth.forgot.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    // AuthFlows card 03: the pass-tone note inside the card, then the back link under it.
    return (
      <AuthLayout>
        <Page narrow title={t("auth.forgot.title")}>
          <div className="card card--auth">
            <p className="authnote" role="status">
              {t("auth.forgot.success")}
            </p>
            <Link className="authcenter" to="/auth/login">
              {t("auth.forgot.back_to_login")}
            </Link>
          </div>
        </Page>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Page narrow title={t("auth.forgot.title")}>
        {features.mail_configured ? null : (
          <Banner tone="warn">
            <p>{t("auth.forgot.mail_disabled")}</p>
          </Banner>
        )}
        <form onSubmit={(event) => void onSubmit(event)} className="card card--auth">
          <Field label={t("auth.forgot.email")} error={error}>
            {({ id, describedBy }) => (
              <input
                id={id}
                aria-describedby={describedBy}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" className="button--auth" disabled={busy || email === ""}>
            {t("auth.forgot.submit")}
          </Button>
          <Link className="authcenter" to="/auth/login">
            {t("auth.forgot.back_to_login")}
          </Link>
        </form>
      </Page>
    </AuthLayout>
  );
}
